import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { lastValueFrom } from 'rxjs';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { QuestionGroupsService } from '../../services/question-groups.service';
import { CreateQuestionDto, QuestionType } from '../../../../core/data/new-dtos/insurance-type.dtos';
import { SharedModule } from '../../../../common/shared.module';

@Component({
    selector: 'app-question-group-modal',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule, DragDropModule],
    templateUrl: './question-group-modal.component.html',
    styleUrls: ['./question-group-modal.component.scss']
})
export class QuestionGroupModalComponent implements OnInit {
    @Input() groupId?: number;
    @Input() initialPhase?: string;

    createForm!: FormGroup;
    questionsToCreate: (CreateQuestionDto & { id?: number })[] = [];
    loading = false;
    QuestionType = QuestionType;

    questionTypeOptions: { label: string; value: QuestionType }[] = [
        { label: 'Texto', value: QuestionType.Text },
        { label: 'Sí/No', value: QuestionType.Bool },
        { label: 'Archivo', value: QuestionType.File },
        { label: 'Condicional', value: QuestionType.Conditional },
    ];

    phaseOptions = [
        { label: 'En el checkout', value: 'Checkout' },
        { label: 'Al Seleccionar', value: 'Selection' }
    ];

    constructor(
        public activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private questionGroupsService: QuestionGroupsService
    ) { }

    async ngOnInit() {
        this.createForm = this.fb.group({
            title: ['', Validators.required],
            description: [''],
            phase: [this.initialPhase || 'Checkout', Validators.required]
        });

        if (this.groupId) {
            await this.loadGroup(this.groupId);
        }
    }

    async loadGroup(id: number) {
        this.loading = true;
        try {
            const group = await lastValueFrom(this.questionGroupsService.getById(id));
            this.createForm.patchValue({
                title: group.title,
                description: group.description
            });

            this.questionsToCreate = group.questions.map(q => {
                // Backend returns string ("Text", "Bool"), we need enum value (0, 1)
                // We cast QuestionType as any to access by string key
                const typeEnum = (QuestionType as any)[q.type];

                return {
                    id: q.id,
                    title: q.title,
                    description: q.description || '',
                    type: typeEnum !== undefined ? typeEnum : QuestionType.Text,
                    isRequired: q.isRequired,
                    cancelOnValue: q.cancelOnValue ?? undefined
                };
            });

        } catch (e) {
            console.error('Error loading group', e);
        } finally {
            this.loading = false;
        }
    }

    addQuestion(): void {
        this.questionsToCreate.push({
            title: '',
            description: '',
            type: QuestionType.Text,
            isRequired: false,
            cancelOnValue: ''
        });
    }

    removeQuestion(index: number): void {
        this.questionsToCreate.splice(index, 1);
    }

    /** Drag and drop: the array order is the order saved on the backend. */
    drop(event: CdkDragDrop<unknown>): void {
        if (event.previousIndex === event.currentIndex) return;
        moveItemInArray(this.questionsToCreate, event.previousIndex, event.currentIndex);
    }

    moveQuestion(index: number, delta: -1 | 1): void {
        const target = index + delta;
        if (target < 0 || target >= this.questionsToCreate.length) return;
        moveItemInArray(this.questionsToCreate, index, target);
    }

    updateQuestion(index: number, field: keyof CreateQuestionDto | 'description', value: any): void {
        (this.questionsToCreate[index] as any)[field] = value;
    }

    async save() {
        if (this.createForm.invalid) return;

        // Filter out questions without titles
        const validQuestions = this.questionsToCreate.filter(q => q.title.trim());

        this.loading = true;
        try {
            const { phase, ...groupData } = this.createForm.value;

            if (this.groupId) {
                await lastValueFrom(
                    this.questionGroupsService.update(this.groupId, {
                        id: this.groupId,
                        ...groupData,
                        questions: validQuestions.map(q => ({ ...q, type: Number(q.type) })) // Ensure numeric enum
                    })
                );
                // Return updated confirmation? Or just close.
                this.activeModal.close({ id: this.groupId, phase });
            } else {
                const newId = await lastValueFrom(
                    this.questionGroupsService.create({
                        ...groupData,
                        questions: validQuestions
                    }),
                );
                this.activeModal.close({ id: newId, phase });
            }

        } catch (e) {
            console.error('Error saving question group', e);
        } finally {
            this.loading = false;
        }
    }

    dismiss() {
        this.activeModal.dismiss();
    }
}
